// Operasyon metadata (keyword) alanlarının Türkçe etiketleri — tek kaynak.
// Hem cihaz raporları sayfası (ReportTab) hem cihaz kartı Operasyon Özeti
// (DeviceCard) buradan okur; böylece etiketler tek yerden yönetilir ve
// aralarında tutarsızlık/drift oluşmaz.
export const METADATA_KEY_LABELS = {
  index: "Göz",
  operationKey: "Operasyon Anahtarı",
  totalCupNumber: "Toplam Bardak Sayısı",
  productName: "Ürün Adı",
  brewTime: "Demleme Süresi (dk)",
  totalWaterTime: "Toplam Su Süresi",
  date: "Tarih",
  startDate: "Başlangıç Tarihi",
  totalBrewVolume: "Toplam Dem Hacmi",
  remainingBrewVolume: "Kalan Dem Hacmi",
  consumentQuota: "Tüketilen Kota",
  drinkQuota: "İçilen Kota",
  drink: "İçilen",
  stalePot: "Bayat Demlik",
  updatedInfo: "Güncelleme Bilgisi",
  type: "Tip",
  program: "Program",
  programDateTime: "Program Tarih",
  // Ek operasyon adımı / metadata alanları (raporda olmayan ama loglarda geçenler)
  // potIndex ürüne göre değişir (Göz / Demlik) — bkz. getMetadataLabel.
  potIndex: "Demlik",
  process: "Süreç",
  temperature: "Sıcaklık",
  waterLevel: "Su Seviyesi",
  cupType: "Fincan Tipi",
  remainingWaterTime: "Kalan Su Süresi",
  waterTime: "Su Süresi",
  potCount: "Demlik Sayısı",
  cupNumber: "Bardak Sayısı",

  // Makinenin (BeverageMachine) yaydığı ve daha önce ÇEVİRİSİZ kalan alanlar.
  // Kaynak: BeverageMachine `metadata: { ... }` literalleri taranarak çıkarıldı.
  amount: "Tutar",
  cleaningTemperature: "Temizlik Sıcaklığı",
  cleaningTime: "Temizlik Süresi",
  cookingStatus: "Pişirme Durumu",
  cupId: "Fincan No",
  currency: "Para Birimi",
  customerReferenceNumber: "Müşteri Referans No",
  errorMessage: "Hata Mesajı",
  formattedDate: "Tarih",
  grinderIndex: "Değirmen No",
  grinderStatus: "Değirmen Durumu",
  intentId: "Ödeme Referansı",
  items: "Kalemler",
  length: "Adet",
  millTimeSeconds: "Öğütme Süresi (sn)",
  nearpayTerminalId: "Terminal No",
  newBrewHour: "Yeni Demleme Saati",
  payBack: "İade",
  potCountTeaFilterCoffee: "Çay/Filtre Kahve Demlik Sayısı",
  potIndexes: "Demlikler",
  previousBrewHour: "Önceki Demleme Saati",
  price: "Fiyat",
  productIndex: "Ürün No",
  quantity: "Adet",
  source: "Kaynak",
  states: "Durumlar",
  status: "Durum",
  userId: "Kullanıcı",
  vessel: "Hazne",
  volume: "Hacim",
};

// Türk kahvesi bağlamı mı? Ürün adından anlaşılmıyorsa operasyon adına bakılır:
// askı (hanger), pişirme (cooking), fincan kalibrasyonu ve öğütme yalnızca
// Fincanda (Türk kahvesi) makinelerinde vardır.
const TURKISH_ONLY_OPERATION_HINTS = [
  "hanger",
  "askı",
  "aski",
  "cooking",
  "pişirme",
  "pisirme",
  "cupcalibration",
  "fincan",
  "grinder",
  "değirmen",
  "degirmen",
  "mixer",
  "karıştırıcı",
  "karistirici",
];

export const isTurkishCoffeeContext = (productName, operationName) => {
  const product = String(productName || "").toLocaleLowerCase("tr-TR");
  if (product.includes("türk")) return true;
  if (product) return false; // ürün belli ve türk değilse çay/filtre kahvedir
  const op = String(operationName || "").toLocaleLowerCase("tr-TR");
  return TURKISH_ONLY_OPERATION_HINTS.some((hint) => op.includes(hint));
};

// "consument" etiketi ürüne göre değişir: Türk Kahvesi'nde "Pişirilen",
// çay/filtre kahvede "Demlenen".
export const getConsumentLabel = (productName) =>
  String(productName || "").toLocaleLowerCase("tr-TR").includes("türk")
    ? "Pişirilen"
    : "Demlenen";

// Bilinmeyen anahtarları okunur hale getir (camelCase/snake_case → boşluklu, baş harf büyük)
export const humanizeMetadataKey = (value) => {
  if (!value) return "-";
  const withSpaces = String(value)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

// Bir metadata anahtarının görünen (Türkçe) etiketi.
//
// `potIndex` ürüne göre değişir: Türk kahvesinde makinedeki birim GÖZ'dür,
// "Demlik" yalnızca çay/filtre kahve tarafında geçerlidir (kullanıcı bildirimi
// 2026-08-13). Ürün adı metadata'da yoksa operasyon adından çıkarılır.
export const getMetadataLabel = (key, productName, operationName) => {
  if (key === "consument") return getConsumentLabel(productName);
  if (key === "potIndex") {
    return isTurkishCoffeeContext(productName, operationName) ? "Göz" : "Demlik";
  }
  if (key === "potCount") {
    return isTurkishCoffeeContext(productName, operationName)
      ? "Göz Sayısı"
      : "Demlik Sayısı";
  }
  return METADATA_KEY_LABELS[key] || humanizeMetadataKey(key);
};

// Operasyon adımı (operationSteps[].type) Türkçe etiketleri — tek kaynak.
// Makinenin (BeverageMachine) yaydığı TÜM adım tipleri burada; yeni tip
// eklendiğinde SADECE buraya eklenir. Tüketiciler: cihaz/firma ReportTab,
// DeviceCard, DeviceOperationsDrawer.
export const STEP_TYPE_LABELS = {
  info: "Bilgi",
  warning: "Uyarı",
  success: "Başarılı",
  failed: "Başarısız",
  fail: "Başarısız",
  error: "Hata",
  processing: "İşleniyor",
  cleaningStarted: "Temizlik Başladı",
  cleaningHangDownProcessing: "Askı İniyor",
  cleaningWaterTaking: "Su Alınıyor",
  cleaningCleanProcessing: "Temizlik Yapılıyor",
  cleaningCompleted: "Temizlik Tamamlandı",
  cleaningFailed: "Temizlik Başarısız",
  cleanStarted: "Temizlik Başladı",
  cleanProcessing: "Temizlik İşleniyor",
  cleanCompleted: "Temizlik Tamamlandı",
  cleanFailed: "Temizlik İptal/Başarısız",
  serviceCleanPotCleaningStarted: "Servis Temizliği Başladı",
  serviceCleanPotCleaningCompleted: "Servis Temizliği Tamamlandı",
  hangerLoweringStarted: "Askı İndirme Başladı",
  hangerLoweringHangDownProcessing: "Askı İniyor",
  hangerLoweringHangDownWaiting: "Askı Aşağıda Bekliyor",
  hangerLoweringCompleted: "Askı İndirme Tamamlandı",
  hangerLoweringFailed: "Askı İndirme Başarısız",
  cookingStarted: "Pişirme Başladı",
  cookingHangDownProcessing: "Askı İniyor",
  cookingWaterTaking: "Su Alınıyor",
  cookingCookProcessing: "Pişirme Devam Ediyor",
  cookingCookCompleted: "Pişirme Tamamlandı",
  cookingFailed: "Pişirme Başarısız",
  cookingQuotaDecreaseProcessing: "Kota Düşürüldü",
  brewStarted: "Demleme Başladı",
  brewProcessing: "Demleme Devam Ediyor",
  brewCompleted: "Demleme Tamamlandı",
  brewFailed: "Demleme Başarısız",
  completedGrinder: "Öğütme Tamamlandı",
  cupCalibrationStarted: "Fincan Kalibrasyonu Başladı",
  cupCalibrationHangerDownProcessing: "Askı İniyor",
  cupCalibrationFillingProcessing: "Su Dolduruluyor",
  cupCalibrationFillingWaiting: "Su Doldurma Bekleniyor",
  cupCalibrationCompleted: "Fincan Kalibrasyonu Tamamlandı",
  cupCalibrationFailed: "Fincan Kalibrasyonu Başarısız",
  hangerCalibrationStarted: "Askı Kalibrasyonu Başladı",
  hangerCalibrationResetting: "Askı Kalibrasyonu Yeniden Başlatıldı",
  hangerCalibrationProcessing: "Askı Hareket Ediyor",
  hangerCalibrationWaiting: "Askı Bekliyor",
  hangerCalibrationCompleted: "Askı Kalibrasyonu Tamamlandı",
  hangerCalibrationFailed: "Askı Kalibrasyonu Başarısız",
  heaterCalibrationStarted: "Isıtıcı Kalibrasyonu Başladı",
  heaterCalibrationHangerDownProcessing: "Fincan Kondu, Askı İniyor",
  heaterCalibrationWaterTaking: "Su Alınıyor",
  heaterCalibrationHeating: "Isıtıcı Çalışıyor",
  heaterCalibrationCompleted: "Isıtıcı Kalibrasyonu Tamamlandı",
  heaterCalibrationFailed: "Isıtıcı Kalibrasyonu Başarısız",
  mixerCalibrationStarted: "Karıştırıcı Kalibrasyonu Başladı",
  mixerCalibrationHangerDownProcessing: "Askı İniyor",
  mixerCalibrationSpinningProcessing: "Karıştırıcı Dönüyor",
  mixerCalibrationSpinningWaiting: "Karıştırıcı Bekliyor",
  mixerCalibrationCompleted: "Karıştırıcı Kalibrasyonu Tamamlandı",
  mixerCalibrationFailed: "Karıştırıcı Kalibrasyonu Başarısız",
  // Ağırlık kalibrasyonu (MassCalibration)
  massCalibrationStarted: "Ağırlık Kalibrasyonu Başladı",
  massCalibrationProcessing: "Ağırlık Kalibrasyonu Devam Ediyor",
  massCalibrationCompleted: "Ağırlık Kalibrasyonu Tamamlandı",
  massCalibrationFailed: "Ağırlık Kalibrasyonu Başarısız",
  // Demlik kalibrasyonu (PotCalibration)
  potCalibrationStarted: "Demlik Kalibrasyonu Başladı",
  potCalibrationProcessing: "Demlik Kalibrasyonu Devam Ediyor",
  potCalibrationCompleted: "Demlik Kalibrasyonu Tamamlandı",
  potCalibrationFailed: "Demlik Kalibrasyonu Başarısız",
  // Bardak tanımlama (CupDefine)
  cupDefineStarted: "Bardak Tanımlama Başladı",
  cupDefineProcessing: "Bardak Tanımlama Devam Ediyor",
  cupDefineFillToggle: "Bardak Doluluk Durumu Değişti",
  cupDefineFilledGlassSaveRequested: "Dolu Bardak Kaydı İstendi",
  cupDefineCompleted: "Bardak Tanımlama Tamamlandı",
  // Şifre / yetki adımları
  wrongPassword: "Hatalı Şifre",
  passwordSuccess: "Şifre Doğrulandı",
  passwordModalClosed: "Şifre Ekranı Kapatıldı",
  // Satın alma / ödeme
  purchaseProductStarted: "Ürün Satın Alma Başladı",
  purchaseProductProcessing: "Ürün Satın Alma İşleniyor",
  purchaseProductCompleted: "Ürün Satın Alma Tamamlandı",
  refund: "İade",
  nearpay_init: "NearPay Başlatma",
  nearpayInitStarted: "NearPay Başlatılıyor",
  nearpayInitProcessing: "NearPay Başlatma Devam Ediyor",
  nearpayInitCompleted: "NearPay Başlatıldı",
  nearpayPurchaseStarted: "Kart Ödemesi Başladı",
  nearpayPurchaseCompleted: "Kart Ödemesi Tamamlandı",
  nearpayCancelPurchaseStarted: "Ödeme İptali Başladı",
  nearpayCancelPurchaseCompleted: "Ödeme İptali Tamamlandı",
  // Ürün tipi adımları
  TEA: "Çay",
  FILTERCOFFEE: "Filtre Kahve",
};

// Operasyon adımı içindeki alan (key) etiketleri — tek kaynak.
export const STEP_KEY_LABELS = {
  operationKey: "Operasyon Anahtarı",
  index: "Göz",
  potIndex: "Demlik",
  process: "Süreç",
  cleaningTemperature: "Temizlik Sıcaklığı",
  cleaningTime: "Temizlik Süresi",
  potCalibrationTime: "Kalibrasyon Süresi",
  waterLevel: "Su Seviyesi",
  temperature: "Sıcaklık",
  lowWater: "Düşük Su",
  unProgramCondition: "Program Yok",
  unQuotaCondition: "Kota Yetersiz",
  comparisonHeatValueTemperature: "Isı Eşiği Uygun",
  potCountTeaFilterCoffee: "Demlik Sayısı",
  potCount: "Demlik Sayısı",
  potIndexes: "Demlik Gözleri",
  massScale: "Ağırlık Ölçeği",
  massValue: "Ağırlık Değeri",
  program: "Program",
  program1: "Sol Program",
  program2: "Orta Program",
  program3: "Sağ Program",
  leftCase: "Sol Koşul",
  middleCase: "Orta Koşul",
  rightCase: "Sağ Koşul",
  productName: "Ürün",
  totalCupNumber: "Toplam Bardak",
  totalConsumption: "Toplam Tüketim",
  totalWaterTime: "Toplam Su Süresi",
  remainingWaterTime: "Kalan Su Süresi",
  brewTime: "Demleme Süresi",
  date: "Tarih",
  startDate: "Başlangıç",
  brewVolumeTotal: "Toplam Demleme Hacmi",
  brewVolumeRemain: "Kalan Demleme Hacmi",
  remainingBrewVolume: "Kalan Demleme Hacmi",
  consumentQuota: "Tüketim Kotası",
  drinkQuota: "İçecek Kotası",
  consument: "Tüketim",
  drink: "İçilen",
  stalePot: "Bayat Demlik",
  cupType: "Fincan Tipi",
  millTimeSeconds: "Öğütme Süresi (sn)",
  grinderIndex: "Değirmen Göz",
  cupAlready: "Fincan Var",
  cupDisabled: "Fincan Devre Dışı",
  brewHour: "Demleme Saati",
  programDate: "Program Tarihi",
  programDateTime: "Program Tarih/Saat",
  formattedDate: "Formatlı Tarih",
  previousBrewHour: "Önceki Demleme Saati",
  newBrewHour: "Yeni Demleme Saati",
  type: "Tür",
  // Ödeme alanları
  intentId: "İşlem No",
  customerReferenceNumber: "Müşteri Referansı",
  price: "Tutar",
  amount: "Tutar",
  currency: "Para Birimi",
  count: "Adet",
  userId: "Kullanıcı",
  paymentMethod: "Ödeme Yöntemi",
  refund: "İade",
};

// Adım tipi / adım alanı için görünen Türkçe etiket (bilinmeyende humanize).
export const getStepLabel = (type) =>
  type ? STEP_TYPE_LABELS[type] || humanizeMetadataKey(type) : "Bilgi";
export const getStepKeyLabel = (key) =>
  STEP_KEY_LABELS[key] || humanizeMetadataKey(key);
